import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Feature } from 'maplibre-gl';
import { map } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { AGENTS_API } from 'src/app/shared/app-token';
import { RegionNamePipe } from 'src/app/shared/pipes/region-name.pipe';
import { Agent } from 'src/app/types/agent';

/**
 * service to get agents from api
 * and return details as maplibre-gl features
 *
 * when not authenticated geolocation is obfuscated (*1) by api service
 *
 * (*1) latitude and longitude get rounded at 3 dezimal numbers
 */
@Injectable( {
    providedIn: 'root'
} )
export class AgentsService {
    /** reference on features list */
    #agentsFeatures: Feature[] = [];

    /** reference on featureCollection */
    agentsCollection = {
        type: 'FeatureCollection',
        features: this.#agentsFeatures
    };

    /** ignore in coverage */
    #apiUrl = inject( AGENTS_API );
    #httpClient = inject( HttpClient );
    #authService = inject( AuthService );
    #regionNamePipe = inject( RegionNamePipe );

    /**
     * method to get agents from api
     * gets called by resolver
     *
     * contructs and returns featureCollection for maplibre-gl
     * when logged-in agent address details are shown in popup
     * countrycode given in data is transformed to translated county displayname
     * @returns list of agents
     */
    getAgents() {
        /** unset list of agent features */
        this.#agentsFeatures = [];
        const loggedIn = this.#authService.isLoggedIn();

        return this.loadAgents().pipe(
            map( ( agents: Agent[] ) => {
                /**  create a GeoJSON Feature for each agent */
                agents.forEach( ( agent: Agent ) => {
                    const markerPopupTemplate = this.createAgentPopup( agent );
                    const showPopUp = !!( agent.address && loggedIn );

                    const feature = {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [agent.address.coordinates.latitude, agent.address.coordinates.longitude]
                        },
                        properties: {
                            color: '#e8b909',
                            popup: showPopUp,
                            description: showPopUp ? markerPopupTemplate : '',
                            ports: agent.ports
                        }
                    } as unknown as Feature;

                    this.#agentsFeatures.push( feature );
                } );
            } ),
            map( () => {
                /** finally set and return data for resolver */
                this.agentsCollection.features = this.#agentsFeatures;
                return this.agentsCollection;
            } )
        );
    }

    /**
     * helper function to create feature popup
     * @param {Agent} agent agent object
     * @returns {string} popup html structure
     */
    createAgentPopup( agent: Agent ) {
        const label_address = $localize`:@@agent-label-address:Address`;
        const label_service = $localize`:@@agent-label-services: Services`;
        const label_air = $localize`:@@agent-port-air:Airfreight`;
        const label_sea = $localize`:@@agent-port-sea:Seafreight`;
        const label_ports_amount = $localize`:@@agent-label-ports-amount:Locations served by the agent`;
        const label_link = $localize`:@@agent-label-link:Show more`;

        const air_icon = agent.airfreight
            ? `<div class="icon-item">
        <span class="icon-img riege-icon material-icons md-16" aria-hidden="true">flight</span>
        <div>${label_air}</div>
    </div>`
            : '';
        const sea_icon = agent.seafreight
            ? `<div class="icon-item">
        <span class="icon-img riege-icon material-icons md-16" aria-hidden="true">directions_boat</span>
        <div>${label_sea}</div>
    </div>`
            : '';
        const popupContainerStart = '<div class="community-map-marker-popup">';
        const popupContainerEnd = '</div>';

        const popupContentStart = '<div class="community-map-marker-popup__card-content">';
        const popupContentEnd = '</div>';

        const popupCardHeader =
            agent.address.name !== undefined
                ? `<div class="community-map-marker-popup__card-header">
                        <div class="headline">${agent.address.name}</div>
        <div class="subline">${this.#regionNamePipe.transform( agent.address.countryCode )}</div>
    </div>`
                : '';
        const popupCardHeading =
            agent.address.name !== undefined
                ? `<div class="heading">${label_address}</div>
        <address class="text">${agent.address.streetOrPOBox} </br>
            ${agent.address.zip} ${agent.address.city}
        </address>`
                : '';
        const popupCardServices = `<div class="heading">${label_service}</div>
        <div class="icon-wrapper">
            ${air_icon}
            ${sea_icon}
        </div>
        <div class="heading">${label_ports_amount}</div>
        <span class="text">${agent.ports?.length}</span>`;

        const markerPopupTemplate =
            popupContainerStart +
            popupCardHeader +
            popupContentStart +
            popupCardHeading +
            popupCardServices +
            popupContentEnd +
            popupContainerEnd;

        return markerPopupTemplate;
    }

    /**
     * actually load the list of agents from api
     * @returns list of agents locations
     */
    loadAgents() {
        return this.#httpClient
            .get<Agent[]>( `${this.#apiUrl}/agents` )
            .pipe( map( ( agents: Agent[] ) => agents.filter( ( agent: Agent ) => agent.address?.coordinates ) ) );
    }
}
